import React, { useCallback, useEffect, useRef, useState } from 'react';
import './HomePromoShelf.css';

export type PromoSlideCta =
  | { type: 'customer_create' }
  | { type: 'executor_catalog' }
  | { type: 'services' }
  | { type: 'orders' }
  | { type: 'profile' }
  | { type: 'external'; url: string }
  | { type: 'none' };

export type PromoSlideAction = {
  label: string;
  cta: PromoSlideCta;
};

export type PromoSlide = {
  id: string;
  /** Числовой id в БД — для трекинга кликов */
  dbId?: number;
  title: string;
  subtitle?: string;
  /** Одна кнопка (старый формат) */
  ctaLabel?: string;
  cta?: PromoSlideCta;
  /** Две кнопки как в макете */
  actions?: PromoSlideAction[];
  showTelegramMark?: boolean;
  tone?: 'blue' | 'violet' | 'teal' | 'amber';
  imageUrl?: string | null;
};

type Props = {
  slides: PromoSlide[];
  intervalMs?: number;
  onCta: (cta: PromoSlideCta, slide: PromoSlide) => void;
};

const TONE_CLASS: Record<NonNullable<PromoSlide['tone']>, string> = {
  blue: 'home-promo__slide--blue',
  violet: 'home-promo__slide--violet',
  teal: 'home-promo__slide--teal',
  amber: 'home-promo__slide--amber'
};

/** Дефолтные слайды в стиле макета */
export function defaultPromoSlides(role: 'customer' | 'executor' | null): PromoSlide[] {
  const findWork: PromoSlide = {
    id: 'fallback-find',
    title: 'Найди исполнителя или зарабатывай на навыках',
    subtitle: 'Все сделки защищены',
    tone: 'blue',
    showTelegramMark: true,
    actions: [
      { label: 'Нужен исполнитель', cta: { type: 'customer_create' } },
      { label: 'Хочу найти заказ', cta: { type: 'executor_catalog' } }
    ]
  };
  const second: PromoSlide =
    role === 'executor'
      ? {
          id: 'fallback-services',
          title: 'Оформи услуги в каталоге',
          subtitle: 'Клиенты заказывают напрямую с твоей карточки',
          tone: 'teal',
          ctaLabel: 'Мои услуги',
          cta: { type: 'services' }
        }
      : {
          id: 'fallback-services',
          title: 'Готовые услуги исполнителей',
          subtitle: 'Выбери услугу и закажи в пару кликов',
          tone: 'teal',
          ctaLabel: 'Смотреть услуги',
          cta: { type: 'services' }
        };
  const third: PromoSlide = {
    id: 'fallback-boost',
    title: 'Больше заказов и выше в поиске',
    subtitle: 'VIP, ТОП и буст через Telegram Stars',
    tone: 'violet',
    ctaLabel: 'В профиль',
    cta: { type: 'profile' }
  };
  return [findWork, second, third];
}

function TelegramMark() {
  return (
    <div className="home-promo__tg" aria-hidden>
      <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
        <path d="M9.8 15.4 9.5 19c.4 0 .6-.2.8-.4l1.9-1.8 3.9 2.9c.7.4 1.2.2 1.4-.7l2.5-11.8c.2-.9-.3-1.3-1-.9L4.3 10.3c-.9.3-.9.9-.2 1.1l4.2 1.3 9.7-6.1c.5-.3.9-.1.5.2l-8.7 8.6Z" />
      </svg>
    </div>
  );
}

export function HomePromoShelf({ slides, intervalMs = 60_000, onCta }: Props) {
  const list = slides.length > 0 ? slides.slice(0, 5) : [];
  const [index, setIndex] = useState(0);
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<number | null>(null);
  const userPausedUntil = useRef(0);

  const clearTimer = () => {
    if (timerRef.current != null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const scrollToIndex = useCallback((i: number, smooth = true) => {
    const el = scrollerRef.current;
    if (!el) return;
    const child = el.children[i] as HTMLElement | undefined;
    if (!child) return;
    el.scrollTo({ left: child.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  const goTo = useCallback(
    (i: number, smooth = true) => {
      const n = list.length;
      if (n <= 0) return;
      const next = ((i % n) + n) % n;
      setIndex(next);
      scrollToIndex(next, smooth);
    },
    [list.length, scrollToIndex]
  );

  const bumpUserInteract = () => {
    userPausedUntil.current = Date.now() + intervalMs;
  };

  useEffect(() => {
    setIndex(0);
    scrollToIndex(0, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.map((s) => s.id).join('|')]);

  useEffect(() => {
    clearTimer();
    if (list.length < 2) return;
    timerRef.current = window.setInterval(() => {
      if (Date.now() < userPausedUntil.current) return;
      setIndex((prev) => {
        const next = (prev + 1) % list.length;
        scrollToIndex(next, true);
        return next;
      });
    }, intervalMs);
    return clearTimer;
  }, [list.length, intervalMs, scrollToIndex]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    let t: number | null = null;
    const onScroll = () => {
      bumpUserInteract();
      if (t != null) window.clearTimeout(t);
      t = window.setTimeout(() => {
        const children = Array.from(el.children) as HTMLElement[];
        if (!children.length) return;
        const left = el.scrollLeft;
        let best = 0;
        let bestDist = Infinity;
        children.forEach((c, i) => {
          const d = Math.abs(c.offsetLeft - left);
          if (d < bestDist) {
            bestDist = d;
            best = i;
          }
        });
        setIndex(best);
      }, 80);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (t != null) window.clearTimeout(t);
    };
  }, [list.length, intervalMs]);

  if (list.length === 0) return null;

  return (
    <section className="home-promo" aria-label="Промо">
      <div className="home-promo__scroller" ref={scrollerRef}>
        {list.map((slide) => {
          const actions: PromoSlideAction[] =
            slide.actions && slide.actions.length > 0
              ? slide.actions
              : slide.cta && slide.cta.type !== 'none' && slide.ctaLabel
                ? [{ label: slide.ctaLabel, cta: slide.cta }]
                : [];
          const title = String(slide.title || '').trim();
          const subtitle = String(slide.subtitle || '').trim();
          const hasCopy = Boolean(title || subtitle);
          /** Баннер-картинка без текста с одной кнопкой: кнопка уже нарисована на самой картинке,
           *  поэтому HTML-кнопку не рисуем — нажатие по всему баннеру ведёт по её ссылке. */
          const wholeCardTap = Boolean(slide.imageUrl) && !hasCopy && actions.length === 1;
          const openWhole = () => {
            bumpUserInteract();
            onCta(actions[0].cta, slide);
          };
          return (
            <article
              key={slide.id}
              className={
                'home-promo__slide ' +
                TONE_CLASS[slide.tone || 'blue'] +
                (slide.imageUrl ? ' home-promo__slide--has-image' : '') +
                (slide.imageUrl && !hasCopy ? ' home-promo__slide--image-only' : '') +
                (wholeCardTap ? ' home-promo__slide--tappable' : '')
              }
              {...(wholeCardTap
                ? {
                    role: 'link',
                    tabIndex: 0,
                    'aria-label': actions[0].label,
                    onClick: openWhole,
                    onKeyDown: (e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openWhole();
                      }
                    }
                  }
                : {})}
              style={
                slide.imageUrl
                  ? {
                      backgroundImage: hasCopy
                        ? `linear-gradient(180deg, rgba(8,12,24,0.08), rgba(8,12,24,0.28)), url(${slide.imageUrl})`
                        : `url(${slide.imageUrl})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center'
                    }
                  : undefined
              }
            >
              {slide.showTelegramMark ? <TelegramMark /> : null}
              {title ? <h2 className="home-promo__title">{title}</h2> : null}
              {subtitle ? <p className="home-promo__sub">{subtitle}</p> : null}
              {actions.length > 0 && !wholeCardTap ? (
                <div className={'home-promo__actions' + (actions.length > 1 ? ' home-promo__actions--dual' : '')}>
                  {actions.map((a) => (
                    <button
                      key={a.label}
                      type="button"
                      className="home-promo__cta"
                      onClick={() => {
                        bumpUserInteract();
                        onCta(a.cta, slide);
                      }}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </article>
          );
        })}
      </div>
      {list.length > 1 ? (
        <div className="home-promo__dots" role="tablist" aria-label="Слайды">
          {list.map((s, i) => (
            <button
              key={s.id}
              type="button"
              role="tab"
              aria-selected={i === index}
              className={'home-promo__dot' + (i === index ? ' home-promo__dot--on' : '')}
              onClick={() => {
                bumpUserInteract();
                goTo(i);
              }}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}
